import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CaseDetail,
  CaseLine,
  CaseListResponse,
  CasePhoto,
  CaseStats,
  Category,
  CreateCaseBody,
  ResolutionType,
  Stage,
  TransitionResponse,
} from './types'

const CASE_KEY = ['material-disposition'] as const

export interface CaseListFilters {
  stage?: Stage
  category?: Category
  resolution_type?: ResolutionType
  vendor_id?: number
  limit?: number
  offset?: number
}

export function useCaseList(filters: CaseListFilters = {}) {
  const params = new URLSearchParams()
  if (filters.stage) params.set('stage', filters.stage)
  if (filters.category) params.set('category', filters.category)
  if (filters.resolution_type) params.set('resolution_type', filters.resolution_type)
  if (filters.vendor_id != null) params.set('vendor_id', String(filters.vendor_id))
  params.set('limit', String(filters.limit ?? 100))
  params.set('offset', String(filters.offset ?? 0))
  const qs = params.toString()

  return useQuery({
    queryKey: [...CASE_KEY, 'list', filters],
    queryFn: () => jsonFetch<CaseListResponse>(`/api/material-disposition?${qs}`),
    staleTime: 15_000,
  })
}

export function useCaseStats() {
  return useQuery({
    queryKey: [...CASE_KEY, 'stats'],
    queryFn: () => jsonFetch<CaseStats>('/api/material-disposition/stats'),
    staleTime: 30_000,
  })
}

export function useCaseDetail(caseId: string | null) {
  return useQuery({
    queryKey: [...CASE_KEY, 'detail', caseId],
    queryFn: () => jsonFetch<CaseDetail>(`/api/material-disposition/${caseId}`),
    enabled: !!caseId,
    staleTime: 5_000,
  })
}

export function useCreateCase() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateCaseBody) =>
      jsonFetch<{ case_id: string; case_number: string; stage: Stage }>(
        '/api/material-disposition',
        { method: 'POST', body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: CASE_KEY }),
  })
}

export interface HeaderEditVars {
  caseId: string
  edits: Record<string, unknown>
}

export function usePatchCaseHeader() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: HeaderEditVars) =>
      jsonFetch<CaseDetail>(`/api/material-disposition/${vars.caseId}`, {
        method: 'PATCH',
        body: vars.edits,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CASE_KEY }),
  })
}

export function useAddCaseLine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { caseId: string; line: Partial<CaseLine> }) =>
      jsonFetch<CaseLine>(`/api/material-disposition/${vars.caseId}/lines`, {
        method: 'POST',
        body: vars.line,
      }),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: [...CASE_KEY, 'detail', vars.caseId] }),
  })
}

export function usePatchCaseLine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { caseId: string; lineId: number; edits: Partial<CaseLine> }) =>
      jsonFetch<CaseLine>(`/api/material-disposition/${vars.caseId}/lines/${vars.lineId}`, {
        method: 'PATCH',
        body: vars.edits,
      }),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: [...CASE_KEY, 'detail', vars.caseId] }),
  })
}

export function useDeleteCaseLine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { caseId: string; lineId: number }) =>
      jsonFetch<{ deleted: boolean }>(
        `/api/material-disposition/${vars.caseId}/lines/${vars.lineId}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: [...CASE_KEY, 'detail', vars.caseId] }),
  })
}

export function useUploadCasePhoto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { caseId: string; file: File; caption?: string }) => {
      const formData = new FormData()
      formData.set('file', vars.file)
      if (vars.caption) formData.set('caption', vars.caption)
      return jsonFetch<CasePhoto>(`/api/material-disposition/${vars.caseId}/photos`, {
        method: 'POST',
        rawBody: formData,
      })
    },
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: [...CASE_KEY, 'detail', vars.caseId] }),
  })
}

export function useDeleteCasePhoto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { caseId: string; documentId: number }) =>
      jsonFetch<{ deleted: boolean }>(
        `/api/material-disposition/${vars.caseId}/photos/${vars.documentId}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: [...CASE_KEY, 'detail', vars.caseId] }),
  })
}

export function useGenerateCasePdf() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (caseId: string) =>
      jsonFetch<{ case_id: string; document_id: number }>(
        `/api/material-disposition/${caseId}/generate-pdf`,
        { method: 'POST' },
      ),
    onSuccess: (_data, caseId) =>
      qc.invalidateQueries({ queryKey: [...CASE_KEY, 'detail', caseId] }),
  })
}

export function casePdfUrl(caseId: string): string {
  return `/api/material-disposition/${caseId}/pdf`
}

export function useTransitionCase() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { caseId: string; targetStage: Stage; note?: string }) => {
      const body: { target_stage: Stage; note?: string } = { target_stage: vars.targetStage }
      if (vars.note) body.note = vars.note
      return jsonFetch<TransitionResponse>(`/api/material-disposition/${vars.caseId}/transition`, {
        method: 'POST',
        body,
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CASE_KEY }),
  })
}

export function useDeleteCase() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (caseId: string) =>
      jsonFetch<{ deleted: string }>(`/api/material-disposition/${caseId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CASE_KEY }),
  })
}
